// Vue directives for component-level authorization
import store from '@/store'
import logger from '@/services/logger'

// Helper function to handle different actions
const handleDirectiveAction = (el, hasAccess, actionConfig, dataAttribute) => {
  // Store original element state if not already stored
  if (!el._originalState) {
    el._originalState = {
      display: el.style.display || '',
      disabled: el.disabled || false,
      className: el.className || '',
      innerHTML: el.innerHTML || ''
    }
  }

  let action, fallbackText
  if (typeof actionConfig === 'string') {
    // Simple string permission/role
    action = 'hide'
  } else if (typeof actionConfig === 'object' && actionConfig.action) {
    // Advanced configuration object
    action = actionConfig.action
    fallbackText = actionConfig.fallbackText
  } else {
    action = 'hide'
  }

  if (hasAccess) {
    // Restore original state
    if (action === 'hide') {
      el.style.display = el._originalState.display
      el.removeAttribute(dataAttribute)
    } else if (action === 'disable') {
      if ('disabled' in el) {
        el.disabled = el._originalState.disabled
      }
      el.classList.remove('auth-disabled')
      el.removeAttribute(dataAttribute)
    } else if (action === 'remove') {
      if (el._replacementComment) {
        el.parentNode?.replaceChild(el, el._replacementComment)
        el._replacementComment = null
      }
      el.removeAttribute(dataAttribute)
    }
  } else {
    // Apply restriction
    if (action === 'hide') {
      el.style.display = 'none'
      el.setAttribute(dataAttribute, 'true')
    } else if (action === 'disable') {
      if ('disabled' in el) {
        el.disabled = true
      }
      el.classList.add('auth-disabled')
      el.setAttribute(dataAttribute, 'true')
    } else if (action === 'remove') {
      if (!el._replacementComment) {
        el._replacementComment = document.createComment(
          fallbackText || `Element hidden due to authorization: ${dataAttribute}`
        )
        el.parentNode?.replaceChild(el._replacementComment, el)
      }
      el.setAttribute(dataAttribute, 'true')
    }
  }
}

// Performance optimization: Store watcher for reactive updates
const setupStoreWatcher = (el, handler) => {
  if (!el._authWatchers) {
    el._authWatchers = []
  }

  // Subscribe to store changes
  if (store && store.subscribe) {
    const unsubscribe = store.subscribe(() => {
      handler()
    })
    // Only push if unsubscribe is actually a function
    if (typeof unsubscribe === 'function') {
      el._authWatchers.push(unsubscribe)
    }
  }
}

const cleanupWatchers = (el) => {
  if (el._authWatchers && Array.isArray(el._authWatchers)) {
    el._authWatchers.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    })
    delete el._authWatchers
  }
}

// Helper function to check if evaluation result has changed
const hasAuthStateChanged = (el, newState) => {
  const oldState = el._lastAuthState
  el._lastAuthState = newState
  return oldState !== newState
}

// Directive to show/hide elements based on user permissions
export const vHasPermission = {
  mounted (el, binding) {
    const config = binding.value
    const permission = typeof config === 'string' ? config : config?.permission

    if (!permission) {
      logger.warn('v-has-permission directive requires a permission string', { binding: binding.value })
      return
    }

    const evaluatePermission = () => {
      const hasPermission = store.getters['authorization/hasPermission'](permission)
      if (hasAuthStateChanged(el, hasPermission)) {
        handleDirectiveAction(el, hasPermission, config, 'data-hidden-by-permission')
      }
    }

    // Initial evaluation
    evaluatePermission()

    // Setup reactive watcher
    setupStoreWatcher(el, evaluatePermission)
  },
  updated (el, binding) {
    const config = binding.value
    const permission = typeof config === 'string' ? config : config?.permission

    if (!permission) {
      logger.warn('v-has-permission directive requires a permission string', { binding: binding.value })
      return
    }

    // Re-evaluate on binding change
    const hasPermission = store.getters['authorization/hasPermission'](permission)
    if (hasAuthStateChanged(el, hasPermission)) {
      handleDirectiveAction(el, hasPermission, config, 'data-hidden-by-permission')
    }
  },
  unmounted (el) {
    // Cleanup
    cleanupWatchers(el)
    if (el._replacementComment && el._replacementComment.parentNode) {
      el._replacementComment.parentNode.removeChild(el._replacementComment)
    }
    delete el._originalState
    delete el._replacementComment
    delete el._lastAuthState
  }
}

// Directive to show/hide elements based on user roles
export const vHasRole = {
  mounted (el, binding) {
    const config = binding.value
    let requiredRoles

    if (typeof config === 'string' || Array.isArray(config)) {
      requiredRoles = Array.isArray(config) ? config : [config]
    } else if (config && config.role) {
      requiredRoles = Array.isArray(config.role) ? config.role : [config.role]
    } else {
      logger.warn('v-has-role directive requires role(s) to be specified', { binding: binding.value })
      return
    }

    const evaluateRole = () => {
      const userRole = store.getters['authorization/userRole']
      const hasRole = requiredRoles.includes(userRole)
      if (hasAuthStateChanged(el, hasRole)) {
        handleDirectiveAction(el, hasRole, config, 'data-hidden-by-role')
      }
    }

    // Initial evaluation
    evaluateRole()

    // Setup reactive watcher
    setupStoreWatcher(el, evaluateRole)
  },
  updated (el, binding) {
    const config = binding.value
    let requiredRoles

    if (typeof config === 'string' || Array.isArray(config)) {
      requiredRoles = Array.isArray(config) ? config : [config]
    } else if (config && config.role) {
      requiredRoles = Array.isArray(config.role) ? config.role : [config.role]
    } else {
      logger.warn('v-has-role directive requires role(s) to be specified', { binding: binding.value })
      return
    }

    // Re-evaluate on binding change
    const userRole = store.getters['authorization/userRole']
    const hasRole = requiredRoles.includes(userRole)
    if (hasAuthStateChanged(el, hasRole)) {
      handleDirectiveAction(el, hasRole, config, 'data-hidden-by-role')
    }
  },
  unmounted (el) {
    // Cleanup
    cleanupWatchers(el)
    if (el._replacementComment && el._replacementComment.parentNode) {
      el._replacementComment.parentNode.removeChild(el._replacementComment)
    }
    delete el._originalState
    delete el._replacementComment
    delete el._lastAuthState
  }
}

// Directive to show/hide elements based on admin status
export const vIsAdmin = {
  mounted (el, binding) {
    const config = binding.value || {}

    const evaluateAdmin = () => {
      const isAdmin = store.getters['authorization/isAdmin']
      if (hasAuthStateChanged(el, isAdmin)) {
        handleDirectiveAction(el, isAdmin, config, 'data-hidden-by-admin')
      }
    }

    // Initial evaluation
    evaluateAdmin()

    // Setup reactive watcher
    setupStoreWatcher(el, evaluateAdmin)
  },
  updated (el, binding) {
    const config = binding.value || {}

    // Re-evaluate on binding change
    const isAdmin = store.getters['authorization/isAdmin']
    if (hasAuthStateChanged(el, isAdmin)) {
      handleDirectiveAction(el, isAdmin, config, 'data-hidden-by-admin')
    }
  },
  unmounted (el) {
    // Cleanup
    cleanupWatchers(el)
    if (el._replacementComment && el._replacementComment.parentNode) {
      el._replacementComment.parentNode.removeChild(el._replacementComment)
    }
    delete el._originalState
    delete el._replacementComment
    delete el._lastAuthState
  }
}

// Directive to show/hide elements based on organization admin status
export const vIsOrgAdmin = {
  mounted (el, binding) {
    const config = binding.value || {}

    const evaluateOrgAdmin = () => {
      const isOrgAdmin = store.getters['authorization/isOrgAdmin']
      if (hasAuthStateChanged(el, isOrgAdmin)) {
        handleDirectiveAction(el, isOrgAdmin, config, 'data-hidden-by-org-admin')
      }
    }

    // Initial evaluation
    evaluateOrgAdmin()

    // Setup reactive watcher
    setupStoreWatcher(el, evaluateOrgAdmin)
  },
  updated (el, binding) {
    const config = binding.value || {}

    // Re-evaluate on binding change
    const isOrgAdmin = store.getters['authorization/isOrgAdmin']
    if (hasAuthStateChanged(el, isOrgAdmin)) {
      handleDirectiveAction(el, isOrgAdmin, config, 'data-hidden-by-org-admin')
    }
  },
  unmounted (el) {
    // Cleanup
    cleanupWatchers(el)
    if (el._replacementComment && el._replacementComment.parentNode) {
      el._replacementComment.parentNode.removeChild(el._replacementComment)
    }
    delete el._originalState
    delete el._replacementComment
    delete el._lastAuthState
  }
}

// Directive to show/hide elements based on organization member status
export const vIsOrgMember = {
  mounted (el, binding) {
    const config = binding.value || {}

    const evaluateOrgMember = () => {
      const isOrgMember = store.getters['authorization/isOrgMember']
      if (hasAuthStateChanged(el, isOrgMember)) {
        handleDirectiveAction(el, isOrgMember, config, 'data-hidden-by-org-member')
      }
    }

    // Initial evaluation
    evaluateOrgMember()

    // Setup reactive watcher
    setupStoreWatcher(el, evaluateOrgMember)
  },
  updated (el, binding) {
    const config = binding.value || {}

    // Re-evaluate on binding change
    const isOrgMember = store.getters['authorization/isOrgMember']
    if (hasAuthStateChanged(el, isOrgMember)) {
      handleDirectiveAction(el, isOrgMember, config, 'data-hidden-by-org-member')
    }
  },
  unmounted (el) {
    // Cleanup
    cleanupWatchers(el)
    if (el._replacementComment && el._replacementComment.parentNode) {
      el._replacementComment.parentNode.removeChild(el._replacementComment)
    }
    delete el._originalState
    delete el._replacementComment
    delete el._lastAuthState
  }
}

// Plugin registration function
export default {
  install (app) {
    app.directive('has-permission', vHasPermission)
    app.directive('has-role', vHasRole)
    app.directive('is-admin', vIsAdmin)
    app.directive('is-org-admin', vIsOrgAdmin)
    app.directive('is-org-member', vIsOrgMember)
  }
}
